import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import { SettingsService } from '../../hooks/SettingsService';
import { LocationsService } from '../../hooks/LocationsService';
import { Loader, DayOfWeekSelect, Title, FarmLocationSelectModal } from '../../elements';
import { LocationOption } from '../../models/locations';
import useAuthContext from '../../hooks/useAuthContext';

import Grid from '@material-ui/core/Grid';
import {
  Icon,
  Card,
  CardContent,
  Button,
  Switch,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Divider,
  Tooltip
} from '@material-ui/core';
import { LoaderSize } from '../../constants/constants';
import SaveIcon from '@material-ui/icons/Save';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { BarChartData, FarmSettingsOptions } from '../../models/settings';
import { InfoOutlined } from '@material-ui/icons';
import UserRole from '../../constants/user-role';

const MAX_CYCLES = 6;
const DEFAULT_DAYS_UNTIL_HOLDING_MOVE = 30;
interface FarmSettingsProps {
  onUpdated?: () => void;
}

// this component represents farm settings options,
// invoked both in common page as well as in modal (TaskLists, FarmSettingsPage, NotificationSettings)
const FarmSettings: React.FC<any> = ({ onUpdated }: FarmSettingsProps) => {
  const classes = useStyles();
  const settingsService = new SettingsService();
  const locationsService = new LocationsService();
  const { state } = useAuthContext();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [barChartData, setBarChartData] = React.useState([] as BarChartData[]);
  const [options, setOptions] = React.useState({} as FarmSettingsOptions);
  const [holdingRoomLocation, setHoldingRoomLocation] = React.useState<LocationOption | null>(null);

  const loadFarmSettingsOptions = async () => {
    setIsBusy(true);

    settingsService
      .getFarmSettings()
      .then((settings) => {
        setBarChartData(settings.dashboardBarChartSettings || []);
        const loadedOptions = settings.options ?? ({} as FarmSettingsOptions);
        setOptions({
          ...loadedOptions,
          daysUntilHoldingMove: loadedOptions.daysUntilHoldingMove || DEFAULT_DAYS_UNTIL_HOLDING_MOVE
        });
        if (settings.options?.holdingRoomLocationId) {
          loadHoldingRoomLocation(settings.options.holdingRoomLocationId);
        }
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const loadHoldingRoomLocation = (locationId: string) => {
    locationsService
      .getLocationById(locationId)
      .then((location) => {
        setHoldingRoomLocation(location);
      })
      .catch(() => {
        // location may have been removed; leave the selector empty
        setHoldingRoomLocation(null);
      });
  };

  const handleHoldingRoomChange = (location: LocationOption | null) => {
    setHoldingRoomLocation(location);
    setOptions({ ...options, holdingRoomLocationId: location?.id ?? null });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadFarmSettingsOptions();
    }
  }, [loaded]);

  const updateFarmSettings = () => {
    if (!validated()) {
      return;
    }

    setIsUpdating(true);

    settingsService
      .updateFarmSettings(barChartData, undefined, options)
      .then(() => {
        notifications.success(t('f-settings-upd-ok'));
        if (onUpdated) {
          onUpdated();
        }
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const validated = (): boolean => {
    if (options.weeksCycle <= 0) {
      notifications.warning(t('f-val-sett-prod-dur'));
      return false;
    }

    if (options.unweanedPigletHoldingMoveEnabled) {
      if (!options.holdingRoomLocationId) {
        notifications.warning(t('f-val-sett-holding-room'));
        return false;
      }

      if (!options.daysUntilHoldingMove || options.daysUntilHoldingMove <= 0) {
        notifications.warning(t('f-val-sett-holding-days'));
        return false;
      }
    }

    return true;
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions({ ...options, [event.target.name]: event.target.checked });
  };

  const handleDayOfWeekChange = (day: number) => {
    setOptions({ ...options, createTaskListDay: day });
  };

  const renderTaskListOptions = () => {
    return (
      <Grid container spacing={2}>
        <Grid item container xs={12} spacing={2} justifyContent="space-between" alignItems="center">
          <Grid item>
            <Box display="flex" alignItems="flex-start">
              <Icon color="primary" fontSize="large">
                {'settings_brightness'}
              </Icon>
              <Box ml={1} mt={0.3}>
                <Title>{t('f-settings')}</Title>
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Button
              color="primary"
              startIcon={<SaveIcon />}
              onClick={updateFarmSettings}
              disabled={state.role === UserRole.user || isUpdating}
              variant="contained"
            >
              {t('f-com-save')}
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography color="primary" style={{ fontWeight: 500 }}>
            {t('f-settings-opt-task')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Tooltip arrow title={t('f-auto-create-tooltip')} className={classes.mr1}>
                <InfoOutlined color="primary" fontSize="small" />
              </Tooltip>
              <Typography>{t('f-settings-opt-task-auto')}</Typography>
            </Box>
            <Switch
              disabled={isUpdating}
              checked={options.taskListWorkerEnabled}
              onChange={handleSwitchChange}
              name="taskListWorkerEnabled"
              color="primary"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            disabled={isUpdating}
            label={t('f-settings-opt-week')}
            size="small"
            variant="outlined"
            fullWidth
            id="prod-cycle-number"
            type="number"
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">{t('f-com-weeks')}</InputAdornment>
            }}
            value={options.weeksCycle}
            onChange={(e) => {
              const newValue = Number(e.target.value);

              // Ensure the new value is within the range 0-MAX_CYCLES
              if (newValue >= 0 && newValue <= MAX_CYCLES) {
                setOptions({ ...options, weeksCycle: newValue });
              }
            }}
            inputProps={{
              max: MAX_CYCLES // Limit the value to MAX_CYCLES
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <DayOfWeekSelect
            disabled={isUpdating}
            variant="outlined"
            fullWidth
            cssClass={classes.dayOfWeek}
            value={options.createTaskListDay || 1}
            onChange={handleDayOfWeekChange}
            key="createTaskListDay"
          />
        </Grid>
      </Grid>
    );
  };

  const renderEarlyWarningOptions = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography color="primary" style={{ fontWeight: 500 }}>
            {t('f-settings-opt-warn')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Tooltip arrow title={t('f-early-warning-enabled-tooltip')} className={classes.mr1}>
                <InfoOutlined color="primary" fontSize="small" />
              </Tooltip>
              <Typography>{t('f-settings-opt-warn-auto')}</Typography>
            </Box>
            <Switch
              disabled={isUpdating}
              checked={options.earlyWarningWorkerEnabled}
              onChange={handleSwitchChange}
              name="earlyWarningWorkerEnabled"
              color="primary"
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderAnimalWorkerOptions = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography color="primary" style={{ fontWeight: 500 }}>
            {t('f-settings-opt-anim')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Tooltip arrow title={t('f-animals-update-tooltip')} className={classes.mr1}>
                <InfoOutlined color="primary" fontSize="small" />
              </Tooltip>
              <Typography>{t('f-settings-opt-anim-auto')}</Typography>
            </Box>
            <Switch
              disabled={isUpdating}
              checked={options.animalsWorkerEnabled}
              onChange={handleSwitchChange}
              name="animalsWorkerEnabled"
              color="primary"
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderHoldingMoveOptions = () => {
    const holdingMoveEnabled = !!options.unweanedPigletHoldingMoveEnabled;

    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Typography color="primary" style={{ fontWeight: 500 }}>
            {t('f-settings-opt-holding')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Tooltip arrow title={t('f-holding-move-enabled-tooltip')} className={classes.mr1}>
                <InfoOutlined color="primary" fontSize="small" />
              </Tooltip>
              <Typography>{t('f-settings-opt-holding-auto')}</Typography>
            </Box>
            <Switch
              disabled={isUpdating}
              checked={holdingMoveEnabled}
              onChange={handleSwitchChange}
              name="unweanedPigletHoldingMoveEnabled"
              color="primary"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            disabled={isUpdating || !holdingMoveEnabled}
            label={t('f-settings-opt-holding-days')}
            size="small"
            variant="outlined"
            fullWidth
            id="days-until-holding-move"
            type="number"
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{t('f-com-days')}</InputAdornment>
            }}
            value={options.daysUntilHoldingMove ?? DEFAULT_DAYS_UNTIL_HOLDING_MOVE}
            onChange={(e) => {
              const newValue = Number(e.target.value);

              if (newValue >= 0) {
                setOptions({ ...options, daysUntilHoldingMove: newValue });
              }
            }}
            inputProps={{
              min: 1
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <FarmLocationSelectModal
            location={holdingRoomLocation}
            onChange={handleHoldingRoomChange}
            selectorId="holdingRoomSelect"
            disableAny={holdingMoveEnabled}
            disabled={isUpdating || !holdingMoveEnabled}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {loaded && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card elevation={1}>
                <CardContent>
                  {renderTaskListOptions()}
                  <Box mt={2} mb={2}>
                    <Divider />
                  </Box>
                  {renderEarlyWarningOptions()}
                  <Box mt={2} mb={2}>
                    <Divider />
                  </Box>
                  {renderAnimalWorkerOptions()}
                  <Box mt={2} mb={2}>
                    <Divider />
                  </Box>
                  {renderHoldingMoveOptions()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default FarmSettings;
